"use client";

import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from "react";
import Image from "next/image";
import Link from "next/link";

import { CompostaOI } from "@/components/article/CompostaOI";
import { TracciaRombi } from "@/components/caso/SegnoCaso";
import { DownloadButton } from "@/components/article/DownloadButton";
import {
  ChiEDoveAperto,
  ComeSiEArrivati,
  CorpoNotizia,
  IlCasoAperto,
  InBreveAperto,
  PerApprofondireAperto,
  RulliAperti,
  blocchiDelCorpo,
  hrefEntita,
  hrefPaese,
  primaFrase,
} from "@/components/article/NotiziaAperta";
import { COMANDO_RIMANDO, ShareBar, newsItemShareTarget } from "@/components/article/ShareBar";
import { LogoOI } from "@/components/common/LogoOI";
import { DisegnoServizio } from "@/components/servizi/catalogo/DisegnoServizio";
import { Icona, type NomeIcona } from "@/components/home/Icona";
import {
  CORNICE_HOME,
  DATI_MONO,
  ETICHETTA,
  ETICHETTA_ACCESA,
  PULSANTE_MARE,
  RIMANDO,
  TITOLO_LINK,
} from "@/components/home/stile";
import { SegnoTitolo } from "@/components/home/TestaSezione";
import { EditorialeSettimana } from "@/components/fasce/EditorialeSettimana";
import { Anello, Imbuto, Posto } from "@/components/paesi/pagina/DallaFonte";
import { PriorityPill } from "@/components/ui/PriorityPill";
import { FUOCO } from "@/components/ui/pulsanti";
import { useAuth } from "@/context/AuthProvider";
import { useRiempiVuoto } from "@/hooks/useRiempiVuoto";
import { ETICHETTA_VISTA, TAPPE_VISIBILI, type DatiCaso, type VistaDispaccio } from "@/lib/caso";
import { PRIORITY_MAP, TIPO_DERIVATO, firmaDi } from "@/lib/constants";
import { fmtDispatchId } from "@/lib/format";
import { risolviLuoghi } from "@/lib/mappaLuoghi";
import { paeseDelLuogo } from "@/lib/paeseDelLuogo";
import { etichettaLuogo } from "@/lib/nomiLuoghi";
import {
  immagineNotizia,
  type AltroCaso,
  type Approfondimento,
  type DataNotizia,
  type RulliNotizia,
} from "@/lib/notizia";
import { TONO_ICONA, iconaCategoria } from "@/lib/prima-pagina";
import { isStrictCountry } from "@/lib/regioni";
import type { SchedaEditoriale } from "@/lib/editoriali-raccolta";
import type { EditorialeRiquadro } from "@/lib/editoriale-settimana";
import { servizioDi } from "@/lib/sorteggio-servizi";
import type { EntityKind, NewsItem } from "@/lib/types";

/* ───────────────────────────────────────────────────────────────
   La pagina del singolo dispaccio (/article/[id]/[slug]).

   Il ramo aperto (`item.free`) sta sul modello del 19/10/2026,
   uploads/notizia-aperta.html + uploads/ISTRUZIONI-notizia-aperta.md, che
   per l'aperto prende il posto dello zip del 15/10. I suoi blocchi sono in
   components/article/NotiziaAperta.tsx: testata (qui sotto, condivisa),
   foto e corpo coi sottotitoli; nella spalla «In breve», «Chi e dove» a
   gruppi `<details>` con «Mostra tutte» in solo CSS, «Dai nostri
   servizi»; sotto, la fascia tinta «Il caso» con la traccia delle tappe e
   il metodo, «Per approfondire» e i rulli per Paese e tema coi segnali
   deboli (`rulliPer` in lib/notizia-dati.ts). Niente mappa, niente
   cronologia nella colonna del testo. «Quanto», «Perché ti riguarda» e la
   didascalia della foto non si rendono: a monte non esistono.

   Il riservato e la vista caso/report del riservato restano sui disegni
   del 15/10/2026:

   - uploads/Pagina della aperto notizia-html.zip: il testo integrale
     (superato per l'aperto dal modello del 19/10, qui sopra);
   - uploads/Notizia articolo chiuso riservata-html.zip: il riservato
     visto da chi non è abbonato;
   - uploads/Caso OI Prima pagina-html.zip: la prima pagina, dove il caso
     compare come etichetta («Caso · Manodopera straniera in Russia») e
     come chip col nome del filone. La vista del caso qui sotto mette
     insieme quell'etichetta, il chip «Il caso · N tappe» e la cronologia
     a rombi dei due disegni della notizia.

   Aperto o chiuso lo dice `item.free`; notizia, caso o report lo dice
   `caso.vista` (lib/caso.ts). Le due cose sono indipendenti: un caso può
   essere riservato, e allora il dispaccio mostra la porta e i collegati
   solo titolo, data e sommario.

   L'impaginazione è quella dei disegni: due colonne (il testo, e a
   destra a 420 pixel le spiegazioni, staccate da un filetto), poi sotto
   a tutta larghezza il lavoro sul caso e gli approfondimenti. Una
   colonna sola sotto `lg` (i disegni passano a una a 1000). Fra 1024 e
   1279 la spalla scende a 340, altrimenti il testo resterebbe a 484.

   Colori e caratteri dalla radice `.oi-home` della rotta: i toni dei
   disegni (carta, superficie, mare, segnale, sonda, filetto) sono i
   token della prima pagina dello stesso giorno, vedi globals.css. Nessun
   colore è scritto qui.
   ─────────────────────────────────────────────────────────────── */

type Props = {
  item: NewsItem;
  /** Il sommario pubblico a frasi intere dei riservati (`sommarioPubblico`
   *  in lib/notizia-dati.ts); `null` sugli aperti. */
  sommarioRiservato: string | null;
  /** Le due frasi che seguono il sommario, per l'attacco che sfuma sopra
   *  «Il dispaccio continua» (`seguitoPubblico` in lib/notizia-dati.ts).
   *  Vuoto o null: la porta non mostra testo, invece di ripetere il
   *  sommario. */
  seguitoRiservato?: string | null;
  /** Il primo paese vero (relazione `countries`, aree escluse). */
  paese: string | null;
  pubblicato: DataNotizia;
  aggiornato: DataNotizia | null;
  approfondimenti: Approfondimento[];
  /** «Altri casi aperti» (`altriCasiPer`), solo sui riservati: è una
   *  fascia del disegno chiuso. Vuoto, la fascia non si rende. */
  altriCasi: AltroCaso[];
  /** I rulli per Paese, tema e segnali deboli in fondo alla notizia
   *  aperta (`rulliPer` + `componiRulli`); `null` sui riservati. */
  rulli: RulliNotizia | null;
  caso: DatiCaso;
  /** L'editoriale della settimana del riquadro grande, solo sui riservati
   *  (`aperturaEditoriali().principale`). */
  editoriale: SchedaEditoriale | null;
  /** L'Atlante degli stretti, reso dal server (i nodi portano icone Lucide
   *  e non attraversano il confine): solo sui riservati. */
  atlante: ReactNode;
  /** I servizi dei riquadri, sorteggiati dalla pagina a ogni richiesta
   *  (lib/sorteggio-servizi.ts) e già distinti fra loro: tre per «Dai
   *  nostri servizi» nella spalla, quattro (uno per domanda) per «I nostri
   *  servizi» dentro «Il caso» degli aperti o il riquadro grande dei
   *  riservati. Questo è un Client Component: un sorteggio qui darebbe un
   *  HTML diverso fra server e browser. */
  servizi: ServiziDispaccio;
  /** L'indirizzo che «Copia link» e «Condividi» danno via. Di serie è
   *  `articleHref(item)`; le versioni storiche passano il proprio
   *  `/article/history/…`, perché il loro `item.id` è l'id della riga di
   *  `/case-histories`, e `/article/<quell'id>` è un altro dispaccio. */
  hrefCondivisione?: string;
  /** Un avviso sopra le targhe: la versione storica ci mette
   *  `AnnuncioVersione`. */
  annuncio?: ReactNode;
  /** Sulle versioni storiche il filone sta nella colonna del testo
   *  («Come ci si è arrivati», modello Zijin del 20/10/2026) e la fascia
   *  «Il caso» non si rende. */
  tappeInColonna?: boolean;
  /** Le schede della coda della spalla sugli aperti: l'editoriale della
   *  settimana, i report vicini al testo e gli annunci (servizi più
   *  l'Accoglienza). Ne compaiono quante ne stanno accanto al testo
   *  (`ColonnaDiCoda`). */
  coda?: CodaSpalla;
};

export type CodaSpalla = {
  editoriale: EditorialeRiquadro | null;
  report: Approfondimento[];
  /** Slug dei servizi da annunciare, già sorteggiati sul server. */
  annunci: string[];
  /** I due servizi del riempitivo sotto il testo (`RiempiTesto`). */
  sottoTesto: string[];
};

/** `vetrina`: i servizi del riquadro grande dei riservati, da uno a tre,
 *  presi fra i quattro di `riquadro` (vedi `VetrinaServizi`). */
export type ServiziDispaccio = { spalla: string[]; riquadro: string[]; vetrina: string[] };

/* ── Regole piccole ─────────────────────────────────────────────── */

/** Lo stesso conto della testata di ArticleView: le parole contate a
 *  monte del taglio (`paroleLettura`), così il badge non descrive il
 *  sommario di un riservato. */
function minutiLettura(item: NewsItem): number {
  const testo = `${item.lead} ${item.body}`.replace(/\s+/g, " ").trim();
  const parole = item.paroleLettura ?? (testo ? testo.split(" ").length : 0);
  return Math.max(1, Math.ceil(parole / 220));
}

/** Il nome del filone: è il prefisso del titolo a monte («Minth in
 *  Serbia: Inđija destina…»), che `caseToNewsItem` mette in `occhiello`.
 *  Manca quando il prefisso è solo la rubrica: allora il caso resta
 *  senza nome, e i titoli dicono soltanto «Il caso» o «Report». */
function nomeCaso(item: NewsItem): string | null {
  return item.occhiello?.trim() || null;
}

/* ── Le classi ripetute ─────────────────────────────────────────── */

/** La testata di un blocco della spalla (`.n-tit`): titolo serif e, a
 *  destra, una riga d'etichetta, sopra un filetto nell'inchiostro. */
const TESTA_BLOCCO =
  "mb-2 flex flex-wrap items-baseline justify-between gap-x-6 gap-y-2 border-b border-ink pb-2.5";
/* 26 pixel a ogni larghezza: `.n-tit h2` e `.r-giorno h2` dei due
   disegni. `text-sezione` (la rampa della prima pagina) a 1440 dà 28. */
const TITOLO_BLOCCO = "m-0 font-serif text-[26px] font-normal leading-[1.15] text-ink";

/** Il riquadro con l'attacco nel rame (`.n-serv`, `.n-duo`, `.nc-porta`,
 *  `.nc-card`): bordo d'inchiostro e quattro pixel di segnale in cima. */
const RIQUADRO = "border border-ink border-t-4 border-t-segnale bg-surface";

/* ── La testata del dispaccio ───────────────────────────────────── */

/** Il chip del caso nella riga delle targhe (`.n-cchip`): porta alla
 *  cronologia, più giù nella stessa pagina. */
function ChipCaso({
  vista,
  nome,
  pezzi,
  inRiga = false,
}: {
  vista: Exclude<VistaDispaccio, "notizia">;
  nome: string | null;
  pezzi: number;
  /** Nella riga del caso di un riservato il chip apre la riga invece di
   *  chiudere quella delle targhe: niente filetto a sinistra, e il nome
   *  si accorcia coi puntini invece di mandare a capo la riga, che deve
   *  restare una sola accanto alle due icone. */
  inRiga?: boolean;
}) {
  const etichetta = ETICHETTA_VISTA[vista];
  return (
    <a
      href="#caso"
      aria-label={`${etichetta}${nome ? `: ${nome}` : ""}, ${pezzi} tappe`}
      className={`group/chip inline-flex items-center gap-3 normal-case tracking-normal no-underline ${
        inRiga ? "min-w-0" : "border-l border-rule pl-4 lg:ml-auto"
      } ${FUOCO}`}
    >
      {/* Nella riga del caso etichetta e nome stanno sulla stessa riga,
          con davanti il segno della vista nel rame: a stringersi è il
          nome, mai l'etichetta né le tappe. Nelle targhe restano impilati. */}
      <span
        className={`flex leading-none ${
          inRiga ? "min-w-0 flex-row items-center gap-2" : "flex-col gap-[3px]"
        }`}
      >
        {/* Nella riga del caso del riservato l'etichetta sale a 17 (era
            13, `text-voce`), vicino al corpo del nome in serif accanto:
            a 13 il cliente la trovava troppo piccola (21/10/2026). Dal
            22/10/2026 sale a 17 anche nel chip delle targhe dell'aperto
            (misurato: la riga cresce di 4 pixel, 38 → 42 a 1440, nessuno
            sbordo a 390). */}
        {inRiga ? (
          <span className="inline-flex shrink-0 items-center gap-2 font-sans text-titolo font-semibold leading-none text-segnale">
            <Icona nome={vista} className="size-[18px]" />
            {etichetta}
          </span>
        ) : (
          <span className="font-sans text-titolo font-semibold text-segnale">{etichetta}</span>
        )}
        {nome ? (
          <b
            className={`bg-[linear-gradient(var(--color-mare),var(--color-mare))] bg-[length:0_1px] bg-left-bottom bg-no-repeat pb-0.5 font-serif text-lettura font-normal leading-[1.1] text-ink transition-[background-size,color] duration-300 group-hover/chip:bg-[length:100%_1px] group-hover/chip:text-mare ${
              inRiga ? "truncate" : ""
            }`}
          >
            {nome}
          </b>
        ) : null}
      </span>
      <span
        className={`inline-flex items-center gap-2 whitespace-nowrap font-sans text-voce font-semibold text-mare ${
          inRiga ? "shrink-0 self-center" : "self-end pb-[3px]"
        }`}
      >
        {/* Sul telefono i rombi lasciano il posto al nome: il conto delle
            tappe resta scritto accanto. */}
        <TracciaRombi pezzi={pezzi} className={inRiga ? "max-sm:hidden" : ""} />
        {pezzi} tappe <span aria-hidden>↗</span>
      </span>
    </a>
  );
}

function TestaDispaccio({ item, paese, pubblicato, aggiornato, deck, caso, hrefCondivisione }: {
  item: NewsItem;
  hrefCondivisione?: string;
  paese: string | null;
  pubblicato: DataNotizia;
  aggiornato: DataNotizia | null;
  deck: string | null;
  caso: DatiCaso;
}) {
  const categoria = item.category.trim();
  const icona = iconaCategoria(categoria);
  const nome = nomeCaso(item);
  const condividi = {
    ...newsItemShareTarget(item),
    ...(hrefCondivisione ? { href: hrefCondivisione } : {}),
  };
  return (
    <header className={`${item.free ? "mb-7" : "mb-4"} flex max-w-[1080px] flex-col gap-[18px]`}>
      {/* Le targhe: il tipo di monte col suo tono (la targa resta quella
          del rullo, anche quando il dispaccio si presenta come report),
          la rubrica col tono del tema, il paese, il contrassegno dei
          riservati e il chip del caso. */}
      <div className={`flex flex-wrap items-center gap-x-[14px] gap-y-2 ${DATI_MONO}`}>
        <PriorityPill priority={item.priority} variant="kicker" />
        {categoria && !item.free ? <span aria-hidden>·</span> : null}
        {categoria ? (
          <span className="inline-flex items-center gap-1.5" style={{ color: TONO_ICONA[icona] }}>
            <Icona nome={icona} />
            {categoria}
          </span>
        ) : null}
        {paese ? (
          <>
            <span aria-hidden>·</span>
            <Link
              href={item.free ? hrefPaese(paese) : hrefEntita("places", paese)}
              className={`inline-flex items-center gap-1.5 font-semibold text-ink no-underline hover:text-mare ${FUOCO}`}
            >
              <Icona nome="paese" className="size-4 text-mare" />
              {etichettaLuogo(paese)}
            </Link>
          </>
        ) : null}
        {item.free ? null : (
          <span className="inline-flex items-center gap-1.5 border border-dashed border-mare px-2 py-0.5 text-targa text-mare">
            <Icona nome="accedi" className="size-[13px]" />
            Accesso riservato
          </span>
        )}
        {/* Sul riservato la data e il codice chiudono la riga delle targhe,
            a destra (istruzioni della notizia riservata): la riga sotto il
            sommario resta al caso e ai due comandi. */}
        {item.free ? null : (
          <span className="tabular-nums md:ml-auto">
            <time dateTime={pubblicato.iso}>{pubblicato.testo}</time>
            {aggiornato ? (
              <>
                {" · Aggiornato il "}
                <time dateTime={aggiornato.iso}>{aggiornato.testo}</time>
              </>
            ) : null}
            {" · "}
            <span className="whitespace-nowrap">{fmtDispatchId(item.id)}</span>
          </span>
        )}
        {caso.vista !== "notizia" && item.free ? <ChipCaso vista={caso.vista} nome={nome} pezzi={caso.pezzi} /> : null}
      </div>

      <h1
        id="titolo-dispaccio"
        className="m-0 font-serif text-apertura font-normal leading-[1.1] tracking-[-0.01em] text-ink"
      >
        {item.title}
      </h1>

      {deck ? (
        <p className="m-0 max-w-[860px] font-serif text-lancio leading-[1.5] text-pretty text-sommesso">{deck}</p>
      ) : null}

      {/* La riga dei dati e i comandi (`.n-meta`): firma, data, codice e,
          sugli aperti, il tempo di lettura; a destra copia, condividi e
          PDF. `no-print` sta sui soli comandi: in stampa la firma e la
          data servono. */}
      {item.free ? (
        <div className="flex flex-wrap items-center justify-between gap-x-8 gap-y-3 border-y border-b-rule border-t-ink py-3.5">
          {/* Spaziatura normale e icone da 14, come `.n-meta` / `.n-azioni`
              del modello Zijin del 20/10/2026: con `tracking-dato` e le
              icone da 18 firma e comandi chiedevano 956 pixel su 900 e i
              comandi andavano a capo, dove nel modello stanno sulla
              stessa riga. */}
          <p className={`m-0 ${DATI_MONO} tracking-normal`}>
            <b className="font-semibold text-ink">{firmaDi(item.author)}</b>
            {" · "}
            <time dateTime={pubblicato.iso}>{pubblicato.testo}</time>
            {aggiornato ? (
              <>
                {" · Aggiornato il "}
                <time dateTime={aggiornato.iso}>{aggiornato.testo}</time>
              </>
            ) : null}
            {" · "}
            <span className="whitespace-nowrap tabular-nums">{fmtDispatchId(item.id)}</span>
            {` · ${minutiLettura(item)} min di lettura`}
          </p>
          <div className="no-print flex flex-wrap items-center gap-x-[22px] gap-y-1.5">
            <ShareBar target={condividi} variante="rimandi" />
            <DownloadButton rimando classe={COMANDO_RIMANDO} />
          </div>
        </div>
      ) : (
        /* La riga del caso di un riservato: senza filetti, e vicina al
           testo che sfuma subito sotto; a sinistra il chip del caso (o la
           firma, se il dispaccio non fa parte di un filone), a destra due icone da 34, copia il link e
           condividi. Una riga sola a ogni larghezza: il nome del caso si
           accorcia, i comandi no. Il PDF non c'è: il testo integrale non è
           nella pagina, e un PDF del solo sommario non serve a nessuno. */
        <div
          data-riga-caso
          className="flex flex-nowrap items-center justify-between gap-4 pt-1"
        >
          {caso.vista !== "notizia" ? (
            <ChipCaso vista={caso.vista} nome={nome} pezzi={caso.pezzi} inRiga />
          ) : (
            <p className={`m-0 min-w-0 ${DATI_MONO}`}>
              <b className="font-semibold text-ink">{firmaDi(item.author)}</b>
            </p>
          )}
          <ShareBar target={condividi} variante="icone" className="no-print" />
        </div>
      )}
    </header>
  );
}

/* ── Il testo e la porta ────────────────────────────────────────── */

function Foto({ item }: { item: NewsItem }) {
  const immagine = immagineNotizia(item);
  return (
    <figure className="m-0 mb-8">
      {/* 21:10 come nel disegno: delle foto dei dispacci (3:2) si vede il
          71% in altezza. Sul telefono, dove la colonna è stretta e il
          ritaglio si nota di più, torna al 3:2 intero. */}
      <div className="relative aspect-[3/2] overflow-hidden rounded-[2px] bg-surface2 md:aspect-[21/10]">
        {immagine.tipo === "foto" ? (
          <Image
            src={immagine.src}
            alt=""
            fill
            preload
            /* La colonna del testo: 900 a 1440, il 62% da `lg`, tutta la
               larghezza sotto. */
            sizes="(min-width: 1440px) 900px, (min-width: 1024px) 62vw, 100vw"
            className="object-cover"
          />
        ) : (
          <CompostaOI className="absolute inset-0 block h-full w-full" />
        )}
      </div>
      {immagine.tipo === "foto" ? (
        <figcaption className={`mt-2.5 flex justify-end ${DATI_MONO} normal-case`}>
          Foto · Osservatorio Italiano
        </figcaption>
      ) : null}
    </figure>
  );
}

/** La porta dei riservati (`.nc-porta`). Il corpo di un riservato non
 *  arriva mai fin qui (lib/nd-transform lo taglia al sommario per
 *  chiunque), quindi la porta non copre niente: dice che cosa c'è di là.
 *
 *  «Accedi» apre il pannello della testata (`oi:apri-accesso`, il
 *  sistema d'accesso del sito). Quell'accesso oggi è dimostrativo, vive
 *  nel `localStorage` del browser, e non sblocca niente: il giorno in cui
 *  `login()` diventerà una sessione verificata dal server sarà la rotta
 *  a decidere se passare il corpo intero. Chi è già «entrato» legge
 *  scritto questo, invece di due comandi che non portano a niente. */
function PortaRiservata({ item, caso }: { item: NewsItem; caso: DatiCaso }) {
  const { user: utente } = useAuth();
  const nome = nomeCaso(item);
  const entita = (["persons", "companies", "institutions", "organizations", "parties", "media"] as EntityKind[]).reduce(
    (n, k) => n + (item.entities[k]?.length ?? 0),
    0
  );
  const filone =
    caso.vista === "notizia" ? null : caso.vista === "caso" ? "la cronologia del caso" : "la cronologia del report";
  const cosa = [
    "Testo integrale",
    "Fonti",
    entita > 0 ? `${entita} ${entita === 1 ? "entità citata" : "entità citate"}` : null,
    caso.vista === "notizia"
      ? null
      : `Cronologia ${caso.vista === "caso" ? "del caso" : "del report"} · ${caso.pezzi} tappe`,
  ].filter((v): v is string => Boolean(v));

  return (
    <div>
      <section
        aria-labelledby="porta-titolo"
        className={`relative mt-11 flex flex-col items-center gap-3 px-12 pb-9 text-center max-md:px-5 max-md:pb-7 ${RIQUADRO}`}
      >
        {/* Il sigillo a cavallo del filetto d'attacco. */}
        <span
          aria-hidden
          className="-mt-[38px] mb-1.5 grid size-[68px] place-items-center rounded-full border border-ink bg-bg shadow-[0_0_0_8px_var(--color-surface)]"
        >
          <LogoOI variant="segno" decorative accento="fill-segnale" className="h-[26px] w-auto text-mare" />
        </span>
        <p className={`m-0 ${ETICHETTA_ACCESA}`}>Riservato agli iscritti</p>
        <h2 id="porta-titolo" className="m-0 max-w-[640px] font-serif text-fascia font-normal leading-[1.2] text-ink md:text-[34px]">
          Per leggere il seguito, iscriviti all&rsquo;Osservatorio.
        </h2>
        <p className="m-0 font-sans text-sommario leading-[1.5] text-sommesso">
          {filone
            ? `Il testo integrale, le fonti e tutta ${filone}${nome ? ` «${nome}»` : ""}.`
            : "Il testo integrale del dispaccio e le sue fonti."}
        </p>
        <ul className="m-0 mb-1 mt-1.5 flex list-none flex-wrap justify-center gap-2 p-0">
          {cosa.map((c) => (
            <li key={c} className="border border-dashed border-sommesso px-2.5 py-1 font-mono text-meta text-ink">
              {c}
            </li>
          ))}
        </ul>
        {utente ? (
          <p className="m-0 mt-1.5 max-w-[560px] font-sans text-sommario leading-[1.5] text-ink">
            Hai effettuato l&rsquo;accesso come {utente.name}. Il testo integrale dei dispacci riservati non è
            ancora abilitato per il tuo profilo.
          </p>
        ) : (
          <div className="mt-1.5 flex flex-wrap items-center justify-center gap-x-6 gap-y-3">
            {/* `.nc-porta .btn`: 15 pixel, 14×32. */}
            <Link
              href="/accoglienza"
              className={`inline-flex items-center justify-center gap-2 rounded-[2px] bg-mare px-8 py-[14px] font-sans text-[15px] font-semibold text-suMare no-underline transition-opacity hover:opacity-90 ${FUOCO}`}
            >
              Iscriviti <span aria-hidden>↗</span>
            </Link>
            <span className="font-sans text-corpo text-sommesso">
              Hai già un accesso?{" "}
              <button
                type="button"
                onClick={() => {
                  // Il pannello sta nella testata, che scorre via con la
                  // pagina: prima si torna in cima, poi si apre.
                  window.scrollTo({ top: 0 });
                  window.dispatchEvent(new Event("oi:apri-accesso"));
                }}
                className={`cursor-pointer bg-transparent p-0 ${RIMANDO}`}
              >
                Accedi <span aria-hidden>↗</span>
              </button>
            </span>
          </div>
        )}
      </section>
    </div>
  );
}

/** Il blocco chiuso (`.nc-chiuso` della tavola «Notizia riservata»): al
 *  posto della foto, l'inizio del testo che sfuma nella carta (`.nc-lead`,
 *  alto al più 150, serif 20 su 35, maiuscoletto sulla prima riga, 90
 *  pixel di sfumatura), il «Il dispaccio continua» fra due tratteggi nel
 *  rame (`.nc-segue`) e la porta. Nessuna foto: su un riservato la tavola
 *  non ne ha, e quella del dispaccio stava in testa come se il pezzo si
 *  potesse leggere.
 *
 *  Il testo che sfuma è il seguito del sommario (19/10/2026): le due
 *  frasi che nel dispaccio vengono dopo quello che la testata ha già
 *  mostrato (`seguitoPubblico`), così la lettura prosegue e si interrompe
 *  nella sfumatura. Prima era lo stesso sommario della testata, ripetuto
 *  tre righe più sotto. Senza seguito (descrizione che finisce col
 *  sommario) la sfumatura non si rende e restano la riga e la porta. */
function BloccoChiuso({ item, attacco, caso }: { item: NewsItem; attacco: string; caso: DatiCaso }) {
  /* Largo quanto la colonna, come la testata sopra e la cronologia sotto
     (`.nc-chiuso` della tavola non ha misura sua): con il `max-w-[860px]`
     di prima, a 1440 la porta finiva a 892 e la testata a 932, cioè due
     bordi destri diversi nella stessa colonna. */
  return (
    <div className="mb-7">
      {attacco ? (
        <div
          aria-hidden
          className="relative max-h-[150px] overflow-hidden after:absolute after:inset-x-0 after:bottom-0 after:h-[90px] after:bg-[linear-gradient(180deg,transparent,var(--color-bg))]"
        >
          {/* Stesso carattere, corpo, interlinea e colore del sommario della
              testata (`TestaDispaccio`): il seguito è la continuazione di
              quel testo, e il maiuscoletto della prima riga e il corpo 20 in
              inchiostro di prima facevano sembrare cambiato il carattere a
              metà lettura (19/10/2026). */}
          <p className="m-0 max-w-[860px] font-serif text-lancio leading-[1.5] text-pretty text-sommesso">
            {attacco}
          </p>
        </div>
      ) : null}
      <p className="m-0 mt-1.5 flex items-center gap-[14px] font-mono text-meta uppercase tracking-[0.08em] text-segnale before:flex-1 before:border-t before:border-dashed before:border-segnale after:flex-1 after:border-t after:border-dashed after:border-segnale">
        <span className="inline-flex items-center gap-[0.4em]">
          {/* «⋯» (U+22EF) non c'è in JetBrains Mono: lo disegnerebbe un
              carattere di ripiego, diverso su ogni macchina. Tre punti
              centrati sulla riga, nel colore della scritta. */}
          <svg aria-hidden viewBox="0 0 14 4" className="h-[0.3em] w-[1.05em] fill-current">
            <circle cx="2" cy="2" r="1.5" />
            <circle cx="7" cy="2" r="1.5" />
            <circle cx="12" cy="2" r="1.5" />
          </svg>
          Il dispaccio continua
        </span>
      </p>
      <PortaRiservata item={item} caso={caso} />
    </div>
  );
}

/* ── La cronologia del caso ─────────────────────────────────────── */

type Tappa =
  | { tipo: "collegato"; id: string; titolo: string; href: string; data: DataNotizia; sommario: string; time: number }
  | { tipo: "questo"; id: string; titolo: string; data: DataNotizia; time: number };

/** La cronologia del caso nella colonna del testo (`.nc-caso`, `.nc-tl`
 *  del disegno chiuso): un filo verticale nel mare, un rombo per tappa,
 *  la data in mono e il titolo in serif. Il rombo pieno nel rame sta sulla
 *  tappa scelta (di serie il dispaccio in pagina); un clic su un titolo la
 *  sceglie e apre sotto il sommario e il collegamento alla sua versione su
 *  /article/history, dove resta il resto del testo, anche quando il caso
 *  è aperto. */
function CronologiaCaso({ item, caso }: { item: NewsItem; caso: DatiCaso }) {
  const [tutte, setTutte] = useState(false);
  /* La tappa scelta: il rombo pieno nel rame sta su di lei. Di serie è il
     dispaccio in pagina. Un clic su un'altra tappa sposta lì il rombo e
     apre sotto il titolo il sommario e «Apri la tappa ↗», invece di
     portare subito su un'altra pagina: chiesto il 20/10/2026, perché col
     salto alla versione il segno nella cronologia che si stava guardando
     non si muoveva mai. */
  const [scelta, setScelta] = useState(item.id);
  /* La tappa sotto il puntatore: il rombo pieno la segue già al passaggio,
     e torna sulla scelta quando il puntatore esce. Un rombo solo è pieno
     in ogni momento, sempre dello stesso rame: prima il passaggio lo
     tingeva di un rame chiaro accanto a quello pieno dell'ultima tappa, e
     si leggevano due rossi diversi. */
  const [sopra, setSopra] = useState<string | null>(null);
  const segnata = sopra ?? scelta;
  const idElenco = useId();
  if (caso.vista === "notizia") return null;
  const nome = nomeCaso(item);
  const tappe: Tappa[] = [
    ...caso.collegati.map((c) => ({ tipo: "collegato" as const, ...c })),
    { tipo: "questo" as const, id: item.id, titolo: item.title, data: caso.questo, time: item.time },
  ].sort((a, b) => a.time - b.time);
  /* Aperti e riservati allo stesso modo: le ultime `TAPPE_VISIBILI` e il
     bottone per le altre, ognuna con data, titolo e collegamento. Fino al
     20/10/2026 il riservato mostrava le ultime quattro e basta; il
     committente ha chiarito che le soglie dei casi e dei report decidono
     come si forma un filone, non quali tappe si vedono. */
  const nascoste = Math.max(0, tappe.length - TAPPE_VISIBILI);
  const mostrate = tutte ? tappe : tappe.slice(nascoste);

  return (
    /* Niente margine sotto: è l'ultimo blocco della colonna, e il vuoto
       verso la fascia dopo lo dà la pagina (60, come la tavola). Con il
       `mb-7` di prima sotto la cronologia restavano 28 pixel di troppo.
       Sul riservato è larga quanto la colonna (`.nc-caso` della tavola),
       con lo stesso bordo destro della testata e della porta; sull'aperto
       resta a 860. */
    <section id="caso" aria-labelledby="caso-titolo" className={`scroll-mt-6 ${item.free ? "max-w-[860px]" : ""}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-3 border-b border-ink pb-2.5">
        <h2 id="caso-titolo" className="m-0 font-serif text-[26px] font-normal leading-[1.2] text-ink">
          <SegnoTitolo />
          {ETICHETTA_VISTA[caso.vista]}
          {nome ? ` · ${nome}` : ""}
        </h2>
        {caso.dal ? (
          <span className={DATI_MONO}>
            {caso.pezzi} tappe · dal <time dateTime={caso.dal.iso}>{caso.dal.testo}</time>
          </span>
        ) : null}
      </div>

      {nascoste > 0 ? (
        <button
          type="button"
          aria-expanded={tutte}
          aria-controls={idElenco}
          onClick={() => setTutte((v) => !v)}
          className={`mt-3.5 cursor-pointer bg-transparent p-0 ${RIMANDO}`}
        >
          {tutte ? "Mostra solo le ultime tappe" : `Mostra le ${nascoste} tappe precedenti`}{" "}
          <span aria-hidden>{tutte ? "↓" : "↑"}</span>
        </button>
      ) : null}

      <ol
        id={idElenco}
        className="relative m-0 mt-[18px] list-none p-0 pl-7 before:absolute before:bottom-6 before:left-[7px] before:top-2.5 before:border-l before:border-mare/50"
      >
        {mostrate.map((t) => {
          const questo = t.tipo === "questo";
          const accesa = t.id === segnata;
          const aperta = t.id === scelta;
          return (
            <li key={`${t.tipo}-${t.id}`} className="relative flex flex-col gap-1 pb-[22px] last:pb-0">
              {/* Tutta la riga sceglie la tappa: rombo, data e titolo. Il
                  rombo sta dentro il bottone, e il suo `before` allarga
                  l'area del clic a 28 pixel, perché è proprio lì che si
                  clicca per primo. */}
              <button
                type="button"
                aria-pressed={aperta}
                onClick={() => setScelta(t.id)}
                onMouseEnter={() => setSopra(t.id)}
                onMouseLeave={() => setSopra(null)}
                className={`group/tappa flex cursor-pointer flex-col gap-1 border-0 bg-transparent p-0 text-left ${FUOCO}`}
              >
                <span
                  aria-hidden
                  className={`absolute -left-[26px] top-[3px] size-2.5 rotate-45 border transition-[transform,background-color] duration-200 before:absolute before:-inset-[9px] before:content-[''] ${
                    accesa ? "scale-125 border-segnale bg-segnale" : "border-mare bg-bg"
                  }`}
                />
                <span
                  className={
                    accesa ? DATI_MONO.replace("text-sommesso", "font-semibold text-segnale") : DATI_MONO
                  }
                >
                  <time dateTime={t.data.iso}>{t.data.testo}</time>
                  {questo ? " · Questo dispaccio" : ""}
                </span>
                <span className="font-serif text-[20px] leading-[1.35] text-ink transition-colors group-hover/tappa:text-mare">
                  {t.titolo}
                </span>
              </button>
              {aperta && t.tipo === "collegato" ? (
                <>
                  {t.sommario ? (
                    <p className="m-0 line-clamp-3 max-w-[760px] font-sans text-sommario leading-[1.5] text-sommesso">
                      {t.sommario}
                    </p>
                  ) : null}
                  <Link href={t.href} className={`mt-1 self-start ${RIMANDO}`}>
                    Apri la tappa <span aria-hidden>↗</span>
                  </Link>
                </>
              ) : null}
            </li>
          );
        })}
      </ol>
    </section>
  );
}

/* ── La spalla ──────────────────────────────────────────────────── */

/** «Contesto» del disegno chiuso (`.nc-ctx`): paese, rubrica e caso come
 *  collegamenti. */
function Contesto({ item, paese, caso }: { item: NewsItem; paese: string | null; caso: DatiCaso }) {
  const categoria = item.category.trim();
  const nome = nomeCaso(item);
  const voci: { chiave: string; href: string; testo: string; icona: NomeIcona; tono?: string }[] = [];
  if (paese) voci.push({ chiave: "paese", href: hrefEntita("places", paese), testo: etichettaLuogo(paese), icona: "paese" });
  if (categoria) {
    const icona = iconaCategoria(categoria);
    voci.push({
      chiave: "cat",
      href: `/rullo?cat=${encodeURIComponent(categoria)}`,
      testo: categoria,
      icona,
      tono: TONO_ICONA[icona],
    });
  }
  if (caso.vista !== "notizia") {
    voci.push({
      chiave: "caso",
      href: "#caso",
      testo: nome ?? ETICHETTA_VISTA[caso.vista],
      icona: "caso",
      tono: "var(--color-segnale)",
    });
  }
  if (voci.length === 0) return null;
  return (
    <section aria-labelledby="contesto">
      <div className={TESTA_BLOCCO}>
        <h2 id="contesto" className={TITOLO_BLOCCO}>
          <SegnoTitolo />
          Contesto
        </h2>
      </div>
      <ul className="m-0 mt-1.5 flex list-none flex-wrap gap-2 p-0">
        {voci.map((v) => (
          <li key={v.chiave}>
            <Link
              href={v.href}
              className={`inline-flex items-center gap-1.5 rounded-[2px] border border-rule px-3 py-[7px] font-sans text-voce text-ink no-underline transition-colors hover:border-mare ${FUOCO}`}
            >
              <Icona nome={v.icona} tono={v.tono} />
              {v.testo}
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

type Gruppo = {
  chiave: string;
  etichetta: string;
  icona: NomeIcona;
  tipo: EntityKind;
  nomi: string[];
  /** Due colonne per i nomi corti (persone, paesi), una per le
   *  istituzioni, che a monte arrivano lunghe. */
  colonne: 1 | 2;
};

/** Quanti nomi per gruppo si vedono prima di «Mostra tutte». */
const NOMI_VISIBILI = 6;

function ElencoNomi({
  nomi,
  tipo,
  colonne,
  conPaese = false,
}: {
  nomi: string[];
  tipo: EntityKind;
  colonne: 1 | 2;
  /** «Luoghi citati»: il Paese accanto al luogo, in grigio, quando lo si
   *  sa davvero (lib/paeseDelLuogo.ts). */
  conPaese?: boolean;
}) {
  return (
    <ul className={`m-0 grid list-none gap-x-4 p-0 ${colonne === 2 ? "grid-cols-2" : "grid-cols-1"}`}>
      {nomi.map((n) => (
        <li key={n} className="min-w-0">
          <Link
            href={hrefEntita(tipo, n)}
            className={`group/n flex items-baseline gap-2.5 py-1.5 font-sans text-sommario leading-[1.3] text-ink no-underline transition-colors hover:text-mare max-md:py-2 ${FUOCO}`}
          >
            {/* Il rombo blu, rame al passaggio. A 5 pixel e al 55% come
                nella tavola si leggeva come un pallino grigio: 6 pixel pieni
                lo fanno leggere per quello che è. */}
            <span
              aria-hidden
              className="size-[6px] shrink-0 -translate-y-0.5 rotate-45 bg-mare transition-colors group-hover/n:bg-segnale"
            />
            <span className="min-w-0 [overflow-wrap:anywhere]">
              {n}
              {conPaese && paeseDelLuogo(n) ? (
                <small className="ml-1.5 font-sans text-voce text-sommesso">{paeseDelLuogo(n)}</small>
              ) : null}
            </span>
          </Link>
        </li>
      ))}
    </ul>
  );
}

function GruppoNomi({ g, coda, conto }: { g: Gruppo; coda?: ReactNode; conto?: string }) {
  const [tutte, setTutte] = useState(false);
  const nascoste = g.nomi.length - NOMI_VISIBILI;
  return (
    /* `data-gruppo`: i gruppi che `bilanciaColonne` può chiudere. */
    <details open data-gruppo id={`g-${g.chiave}`} className="group/g scroll-mt-6 border-b border-rule">
      <summary
        className={`flex cursor-pointer list-none items-center gap-2.5 py-3.5 font-sans text-corpo font-semibold text-ink [&::-webkit-details-marker]:hidden ${FUOCO}`}
      >
        <Icona nome={g.icona} className="size-5 text-mare" />
        {g.etichetta}
        <span className="ml-auto font-mono text-meta font-normal tabular-nums text-sommesso">{conto ?? g.nomi.length}</span>
        <span
          aria-hidden
          className="ml-1 mr-0.5 -mt-1 size-[7px] rotate-45 border-b-[1.5px] border-r-[1.5px] border-sommesso transition-transform group-open/g:mt-1 group-open/g:-rotate-[135deg]"
        />
      </summary>
      <ElencoNomi
        nomi={tutte || nascoste <= 0 ? g.nomi : g.nomi.slice(0, NOMI_VISIBILI)}
        tipo={g.tipo}
        colonne={g.colonne}
      />
      {nascoste > 0 ? (
        <button
          type="button"
          aria-expanded={tutte}
          onClick={() => setTutte((v) => !v)}
          className={`mb-3.5 mt-1 block cursor-pointer border-0 bg-transparent p-0 font-sans text-voce font-semibold text-mare ${FUOCO}`}
        >
          {tutte ? "Mostra meno ↑" : `Mostra tutte (${g.nomi.length}) ↓`}
        </button>
      ) : (
        <div className="h-3" />
      )}
      {coda}
    </details>
  );
}

/** «Luoghi citati», in coda al gruppo dei paesi: due colonne, il Paese
 *  in grigio accanto al luogo. Sul riservato si ferma a `NOMI_VISIBILI`
 *  come gli altri gruppi (istruzioni della notizia riservata: al più sei
 *  nomi per gruppo), col suo «Mostra tutti»; sull'aperto resta intero. */
function LuoghiCitati({ citati, tetto }: { citati: string[]; tetto: boolean }) {
  const [tutti, setTutti] = useState(false);
  const nascosti = tetto ? citati.length - NOMI_VISIBILI : 0;
  return (
    <div className="-mt-1 mb-3.5 border-t border-dashed border-rule pt-3">
      <p className="m-0 mb-1 flex items-center justify-between">
        <span className={`inline-flex items-center gap-1.5 ${ETICHETTA}`}>
          <Icona nome="luogo" className="size-[18px] text-mare" />
          Luoghi citati
        </span>
        <span className="font-mono text-meta text-sommesso">{citati.length}</span>
      </p>
      <ElencoNomi
        nomi={tutti || nascosti <= 0 ? citati : citati.slice(0, NOMI_VISIBILI)}
        tipo="places"
        colonne={2}
        conPaese
      />
      {nascosti > 0 ? (
        <button
          type="button"
          aria-expanded={tutti}
          onClick={() => setTutti((v) => !v)}
          className={`mt-1 block cursor-pointer border-0 bg-transparent p-0 font-sans text-voce font-semibold text-mare ${FUOCO}`}
        >
          {tutti ? "Mostra meno ↑" : `Mostra tutti (${citati.length}) ↓`}
        </button>
      ) : null}
    </div>
  );
}

/** «Chi e dove» (`.n-conti` più i gruppi `.n-grp`): i quattro conteggi in
 *  cima portano ai gruppi qui sotto, ogni nome apre l'archivio. I luoghi
 *  si dividono in paesi (quelli che la mappa sa disegnare) e luoghi
 *  citati (città, regioni): il disegno li scrive «4 + 8», e sul riservato
 *  la riga del gruppo dice lo stesso (il riquadro dei conti in cima li
 *  somma, e il solo numero dei paesi lì accanto sembrava contraddirlo). */
function ChiEDove({ item, riservato = false }: { item: NewsItem; riservato?: boolean }) {
  const e = item.entities;
  const luoghi = useMemo(
    () => [...new Set((e.places ?? []).map(etichettaLuogo).filter((l) => isStrictCountry(l)))],
    [e.places]
  );
  const { paesi, citati } = useMemo(() => {
    const paesi: string[] = [];
    const citati: string[] = [];
    for (const l of luoghi) (risolviLuoghi([l]).mentionedISO.length > 0 ? paesi : citati).push(l);
    return { paesi, citati };
  }, [luoghi]);

  const istituzioni = [...(e.institutions ?? []), ...(e.organizations ?? []), ...(e.parties ?? [])];
  const gruppi: Gruppo[] = [
    { chiave: "per", etichetta: "Persone", icona: "persona" as const, tipo: "persons" as const, nomi: e.persons ?? [], colonne: 2 as const },
    { chiave: "az", etichetta: "Aziende", icona: "azienda" as const, tipo: "companies" as const, nomi: e.companies ?? [], colonne: 1 as const },
    {
      chiave: "ist",
      etichetta: "Istituzioni e organizzazioni",
      icona: "istituzione" as const,
      tipo: "institutions" as const,
      nomi: istituzioni,
      colonne: 1 as const,
    },
  ].filter((g) => g.nomi.length > 0);

  const conti = [
    { chiave: "per", etichetta: "Persone", icona: "persona" as NomeIcona, n: e.persons?.length ?? 0 },
    { chiave: "az", etichetta: "Aziende", icona: "azienda" as NomeIcona, n: e.companies?.length ?? 0 },
    { chiave: "ist", etichetta: "Istituzioni", icona: "istituzione" as NomeIcona, n: istituzioni.length },
    { chiave: "pae", etichetta: "Paesi", icona: "paese" as NomeIcona, n: paesi.length + citati.length },
  ].filter((c) => c.n > 0);

  return (
    <section aria-labelledby="chi-e-dove">
      <div className={TESTA_BLOCCO}>
        <h2 id="chi-e-dove" className={TITOLO_BLOCCO}>
          <SegnoTitolo />
          Chi e dove
        </h2>
        <span className={ETICHETTA}>Ogni nome apre l&rsquo;archivio</span>
      </div>
      {conti.length === 0 ? (
        <p className="m-0 mt-3 font-sans text-sommario text-sommesso">
          Il dispaccio non cita persone, aziende, istituzioni o luoghi.
        </p>
      ) : (
        <>
          <nav
            aria-label="Riepilogo delle entità"
            className="mb-1 mt-3.5 grid rounded-[2px] border border-rule bg-surface"
            style={{ gridTemplateColumns: `repeat(${conti.length}, minmax(0, 1fr))` }}
          >
            {conti.map((c, i) => (
              <a
                key={c.chiave}
                href={`#g-${c.chiave}`}
                className={`flex flex-col items-center gap-0.5 px-1 py-2.5 text-ink no-underline transition-colors hover:bg-petrolio ${
                  i > 0 ? "border-l border-rule" : ""
                } ${FUOCO}`}
              >
                <Icona nome={c.icona} className="mb-1 size-5 text-mare" />
                <b className="font-serif text-[26px] font-normal leading-none">{c.n}</b>
                <span className="font-sans text-voce text-sommesso">{c.etichetta}</span>
              </a>
            ))}
          </nav>
          {gruppi.map((g) => (
            <GruppoNomi key={g.chiave} g={g} />
          ))}
          {paesi.length + citati.length > 0 ? (
            <GruppoNomi
              g={{
                chiave: "pae",
                etichetta: "Paesi e luoghi",
                icona: "paese",
                tipo: "places",
                nomi: paesi.length > 0 ? paesi : citati,
                colonne: 2,
              }}
              conto={riservato && paesi.length > 0 && citati.length > 0 ? `${paesi.length} + ${citati.length}` : undefined}
              coda={
                paesi.length > 0 && citati.length > 0 ? <LuoghiCitati citati={citati} tetto={riservato} /> : null
              }
            />
          ) : null}
        </>
      )}
    </section>
  );
}

type RigaServizio = { slug: string; titolo: string; testo: string; icona: NomeIcona };

/** I testi scritti per la spalla (`.n-serv`), per i tre servizi che la
 *  spalla mostrava fissi fino al 19/10/2026. Da allora i tre servizi sono
 *  sorteggiati dalla pagina fra i quattordici: quando esce uno di questi
 *  vale la sua riga, per gli altri titolo, descrizione e icona del catalogo
 *  (`righeServizi`). */
const SERVIZI_SPALLA: Record<string, Omit<RigaServizio, "slug">> = {
  "due-diligence": {
    titolo: "Due diligence",
    testo: "Assetti, legami e contenziosi delle controparti, ricostruiti fonte per fonte.",
    icona: "cerca",
  },
  monitoraggio: {
    titolo: "Monitoraggio",
    testo: "Ogni mattina i dispacci che toccano il tuo Paese, la tua filiera e le tue controparti.",
    icona: "segnale",
  },
  analisi: {
    titolo: "Analisi Paese",
    testo: "Mercati, costi e scenari messi in cifre, per chi investe all'estero.",
    icona: "report",
  },
};

/** Le righe di un elenco di servizi: i testi scritti per il riquadro
 *  quando ci sono, altrimenti quelli del catalogo. Uno slug fuori catalogo
 *  salta. */
function righeServizi(slugs: string[], scritti: Record<string, Omit<RigaServizio, "slug">>): RigaServizio[] {
  return slugs.flatMap((slug) => {
    const proprio = scritti[slug];
    if (proprio) return [{ slug, ...proprio }];
    const s = servizioDi(slug);
    return s ? [{ slug, titolo: s.titolo, testo: s.descrizione, icona: s.icona }] : [];
  });
}

function ServiziSpalla({
  paese,
  allunga = false,
  slugs,
}: {
  paese: string | null;
  allunga?: boolean;
  slugs: string[];
}) {
  return (
    /* `allunga` (riservati): il riquadro scende fino al fondo della colonna
       del testo, col bottone in basso, così le due colonne finiscono
       insieme (vedi `bilanciaColonne`). */
    <section
      aria-labelledby="dai-servizi"
      className={`px-6 pb-6 pt-[22px] ${allunga ? "lg:flex lg:flex-1 lg:flex-col" : ""} ${RIQUADRO}`}
    >
      <p className={`m-0 ${ETICHETTA_ACCESA}`}>Dai nostri servizi</p>
      <h2 id="dai-servizi" className="m-0 mb-1.5 mt-2 font-serif text-[26px] font-normal leading-[1.15] text-ink">
        {paese ? `${etichettaLuogo(paese)}: lavori su questo mercato?` : "Lavori su questi mercati?"}
      </h2>
      <p className="m-0 mb-3.5 font-sans text-sommario leading-[1.45] text-sommesso">
        L&rsquo;Osservatorio affianca le imprese italiane prima delle decisioni.
      </p>
      <ul className="m-0 list-none border-t border-ink p-0">
        {righeServizi(slugs, SERVIZI_SPALLA).map((s) => (
          <li key={s.slug} className="border-b border-rule">
            <Link
              href={`/servizi/${s.slug}`}
              className={`group/s grid grid-cols-[24px_minmax(0,1fr)_auto] items-start gap-3 py-3.5 text-ink no-underline ${FUOCO}`}
            >
              <Icona nome={s.icona} className="mt-px size-6 text-mare" />
              <span>
                <b className="block font-serif text-lettura font-normal leading-[1.2] transition-colors group-hover/s:text-mare">
                  {s.titolo}
                </b>
                <small className="mt-[3px] block font-sans text-sommario leading-[1.4] text-sommesso">{s.testo}</small>
              </span>
              <span aria-hidden className="mt-0.5 font-sans text-corpo font-semibold text-mare">
                ↗
              </span>
            </Link>
          </li>
        ))}
      </ul>
      <div className="mt-[18px] flex flex-wrap items-center gap-x-4 gap-y-2.5 lg:mt-auto lg:pt-[18px]">
        <Link href="/contatti" className={PULSANTE_MARE}>
          Apri una conversazione <span aria-hidden>↗</span>
        </Link>
        <span className="font-sans text-voce text-sommesso">Riservata, sotto NDA</span>
      </div>
    </section>
  );
}

/* ── La coda della spalla (aperti) ──────────────────────────────── */

/** I report vicini al testo come una tabella sola, non una scheda per
 *  report (richiesta del 20/10/2026: «non va bene spezzettare le
 *  informazioni così, serve una tabella tecnica»). Colonne Data / Report /
 *  Paese, il codice OI sotto il titolo, filetti fra le righe. Le righe
 *  portano `data-riga`: se la tabella non sta tutta accanto al testo,
 *  `ColonnaDiCoda` ne toglie dal fondo invece di toglierla intera. */
function TabellaReport({ righe }: { righe: Approfondimento[] }) {
  return (
    <section aria-labelledby="report-vicini" className="border border-ink border-t-[3px] border-t-mare bg-surface">
      <div className="flex items-baseline justify-between gap-4 border-b border-ink px-4 py-3">
        <h2 id="report-vicini" className={`m-0 ${ETICHETTA_ACCESA}`}>
          Report collegati
        </h2>
        <Link href="/report" className={`font-sans text-voce font-semibold text-mare no-underline hover:underline ${FUOCO}`}>
          Tutti i report <span aria-hidden>↗</span>
        </Link>
      </div>
      <table className="w-full border-collapse text-left">
        <thead>
          <tr className={`border-b border-rule ${DATI_MONO}`}>
            <th scope="col" className="w-[76px] px-4 py-2 font-normal">Data</th>
            <th scope="col" className="py-2 pr-3 font-normal">Report</th>
            <th scope="col" className="w-[84px] py-2 pr-4 font-normal">Paese</th>
          </tr>
        </thead>
        <tbody>
          {righe.map((r) => (
            <tr key={r.id} data-riga className="border-b border-rule align-top last:border-b-0">
              <td className="px-4 py-2.5 font-mono text-meta uppercase tracking-dato text-sommesso">
                <time dateTime={r.data.iso}>{r.data.testo}</time>
              </td>
              <td className="py-2.5 pr-3">
                <Link href={r.href} className={`block font-serif text-[15px] leading-[1.3] ${TITOLO_LINK}`}>
                  {r.titolo}
                </Link>
                <span className="mt-1 block font-mono text-meta tabular-nums text-sommesso">{fmtDispatchId(r.id)}</span>
              </td>
              <td className="py-2.5 pr-4 font-mono text-meta uppercase tracking-dato text-ink">
                {r.territorio ? etichettaLuogo(r.territorio) : "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

/** Un annuncio della casa: un servizio col suo disegno del catalogo, o
 *  l'Accoglienza. Etichetta «Annuncio» in chiaro: è pubblicità nostra, e
 *  chi legge deve distinguerla dalle notizie. */
function SchedaAnnuncio({ slug }: { slug: string }) {
  if (slug === "accoglienza") {
    return (
      <aside aria-label="Annuncio" className="flex flex-col gap-2.5 bg-fasciaMare px-[22px] py-5 text-suFasciaMare">
        <p className="m-0 font-sans text-voce font-semibold opacity-80">Annuncio · Accoglienza</p>
        <p className="m-0 font-serif text-[26px] leading-[1.1]">Chiamateci, 7 giorni su 7.</p>
        <p className="m-0 font-sans text-sommario leading-[1.45] opacity-90">
          Una persona, non un centralino. Quello che ci racconti resta riservato.
        </p>
        <Link href="/accoglienza" className={`mt-1 self-start font-sans text-corpo font-semibold text-suFasciaMare underline-offset-4 hover:underline ${FUOCO}`}>
          Apri l&rsquo;Accoglienza <span aria-hidden>↗</span>
        </Link>
      </aside>
    );
  }
  const s = servizioDi(slug);
  if (!s) return null;
  return (
    <aside aria-label="Annuncio" className="border border-ink bg-surface">
      <Link href={`/servizi/${s.slug}`} className={`group/a flex flex-col text-ink no-underline ${FUOCO}`}>
        <span className="block aspect-[360/150] border-b border-rule bg-[var(--oi-sv-fondo)]">
          <DisegnoServizio slug={s.slug} />
        </span>
        <span className="flex flex-col gap-1.5 px-[22px] pb-5 pt-4">
          <span className="font-sans text-voce font-semibold text-sommesso">Annuncio · Servizio {s.numero}</span>
          <span className="font-sans text-voce font-semibold text-segnale">{s.frase}</span>
          <b className="font-serif text-[22px] font-normal leading-[1.2] transition-colors group-hover/a:text-mare">{s.titolo}</b>
          <span className="line-clamp-3 font-sans text-sommario leading-[1.45] text-sommesso">{s.descrizione}</span>
          <span className="mt-1 font-sans text-corpo font-semibold text-mare">
            Scopri il servizio <span aria-hidden>↗</span>
          </span>
        </span>
      </Link>
    </aside>
  );
}

/** Il riempitivo sotto il testo, quando la spalla è più lunga del testo
 *  (richiesta del committente del 20/10/2026: «quando il testo finisce c'è
 *  un bianco, riempirlo con una pubblicità, un servizio, AI, cibernetica,
 *  il chi siamo, con tutte le forme del sito»). Stesso meccanismo della
 *  coda della spalla (`useRiempiVuoto`): ci stanno quante schede servono
 *  a chiudere il bianco, mai una di più, e la pagina non si allunga.
 *  Le forme sono quelle del sito: la scheda servizio col suo disegno del
 *  catalogo (`DisegnoServizio`), i tre manuali coi disegni di «Dalla fonte
 *  alla decisione» della pagina Paese (`Imbuto`, `Anello`, `Posto`), il
 *  segno OI per «Chi siamo». Solo da `lg`. */
function RiempiTesto({ servizi }: { servizi: string[] }) {
  const scatola = useRef<HTMLDivElement>(null);
  const [primo, secondo] = servizi;
  const schede: { k: string; largo?: boolean; nodo: ReactNode }[] = [];
  if (primo) schede.push({ k: `s${primo}`, largo: true, nodo: <ServizioLargo slug={primo} /> });
  schede.push(
    {
      k: "osint",
      nodo: (
        <SchedaManuale etichetta="OSINT · Il metodo" titolo="Dalla fonte all’informazione utile." href="/osint" rimando="Leggi il manuale OSINT">
          <Imbuto />
        </SchedaManuale>
      ),
    },
    {
      k: "cib",
      nodo: (
        <SchedaManuale etichetta="Cibernetica aziendale" titolo="Il problema ce l’hai già. Il numero arriva dopo." href="/cibernetica" rimando="Scopri la cibernetica">
          <Anello />
        </SchedaManuale>
      ),
    },
    {
      k: "ai",
      nodo: (
        <SchedaManuale etichetta="AI · Percorso per PMI" titolo="Il sistema, prima dell’AI." href="/ai" rimando="Il percorso di adozione">
          <Posto />
        </SchedaManuale>
      ),
    },
    {
      k: "chi",
      nodo: (
        <SchedaManuale etichetta="Chi siamo" titolo="Una visione propria, i mezzi per costruirla." href="/chi-siamo" rimando="Conosci l’Osservatorio">
          <span className="my-1.5 flex aspect-[300/120] items-center justify-center border border-rule bg-[var(--oi-sv-fondo)]">
            <LogoOI variant="segno" decorative accento="fill-segnale" className="h-16 w-auto text-mare" />
          </span>
        </SchedaManuale>
      ),
    },
  );
  // In coda le forme via via più basse (servizio ~230, Accoglienza ~110,
  // striscia dei rimandi ~55): dove le grandi non entrano, chiudono il
  // bianco quelle piccole, e OSINT, cibernetica, AI e chi siamo restano
  // comunque in pagina con la striscia.
  if (secondo) schede.push({ k: `s${secondo}`, largo: true, nodo: <ServizioLargo slug={secondo} /> });
  schede.push({ k: "acc", largo: true, nodo: <FasciaAccoglienza /> });
  schede.push({ k: "strip", largo: true, nodo: <StrisciaRimandi /> });
  useRiempiVuoto(scatola);
  return (
    <div ref={scatola} aria-label="Dall’Osservatorio" role="region" className="relative mt-10 hidden flex-1 lg:block">
      <div className="absolute inset-0 grid grid-cols-2 content-start gap-6 overflow-hidden">
        {schede.map((x) => (
          <div key={x.k} className={x.largo ? "col-span-2" : ""}>
            {x.nodo}
          </div>
        ))}
      </div>
    </div>
  );
}

/** I rimandi alle pagine del metodo in una riga sola, con il segno OI:
 *  la forma più bassa del riempitivo. */
const RIMANDI_OSSERVATORIO: [string, string][] = [
  ["OSINT", "/osint"],
  ["Cibernetica", "/cibernetica"],
  ["AI per le PMI", "/ai"],
  ["La lavagna", "/lavagna"],
  ["Chi siamo", "/chi-siamo"],
  ["Accoglienza 7/7", "/accoglienza"],
];
/** L'Accoglienza in una fascia bassa a tutta larghezza della colonna. */
function FasciaAccoglienza() {
  return (
    <aside
      aria-label="Annuncio"
      className="flex flex-wrap items-center justify-between gap-x-8 gap-y-3 bg-fasciaMare px-6 py-5 text-suFasciaMare"
    >
      <span className="flex flex-col gap-1">
        <span className="font-sans text-voce font-semibold opacity-80">Annuncio · Accoglienza 7/7</span>
        <span className="font-serif text-[26px] leading-[1.1]">Chiamateci, 7 giorni su 7.</span>
      </span>
      <Link
        href="/accoglienza"
        className={`font-sans text-corpo font-semibold text-suFasciaMare underline-offset-4 hover:underline ${FUOCO}`}
      >
        Una persona, non un centralino <span aria-hidden>↗</span>
      </Link>
    </aside>
  );
}

function StrisciaRimandi() {
  return (
    <nav aria-label="Dall’Osservatorio" className="flex flex-wrap items-center gap-x-5 gap-y-2 border-y border-ink px-1 py-3.5">
      <LogoOI variant="segno" decorative accento="fill-segnale" className="h-4 w-auto text-mare" />
      {RIMANDI_OSSERVATORIO.map(([t, h]) => (
        <Link key={h} href={h} className={RIMANDO}>
          {t} <span aria-hidden>↗</span>
        </Link>
      ))}
    </nav>
  );
}

/** Un manuale o una pagina del sito come scheda: etichetta rame, titolo
 *  serif, il disegno, il rimando. */
function SchedaManuale({
  etichetta,
  titolo,
  href,
  rimando,
  children,
}: {
  etichetta: string;
  titolo: string;
  href: string;
  rimando: string;
  children: ReactNode;
}) {
  return (
    <article className="flex h-full flex-col gap-2 border border-ink border-t-[3px] border-t-segnale bg-surface px-5 pb-5 pt-4">
      <p className={`m-0 ${ETICHETTA_ACCESA}`}>{etichetta}</p>
      <h3 className="m-0 font-serif text-[21px] font-normal leading-[1.2] text-ink">
        <Link href={href} className={TITOLO_LINK}>
          {titolo}
        </Link>
      </h3>
      {children}
      <Link href={href} className={`mt-auto self-start ${RIMANDO}`}>
        {rimando} <span aria-hidden>↗</span>
      </Link>
    </article>
  );
}

/** Un servizio a tutta larghezza della colonna del testo: disegno del
 *  catalogo a sinistra, testi a destra. Marcato «Annuncio». */
function ServizioLargo({ slug }: { slug: string }) {
  const s = servizioDi(slug);
  if (!s) return null;
  return (
    <aside aria-label="Annuncio" className="border border-ink bg-surface">
      <Link href={`/servizi/${s.slug}`} className={`group/a grid grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] text-ink no-underline ${FUOCO}`}>
        <span className="block border-r border-rule bg-[var(--oi-sv-fondo)]">
          <span className="block aspect-[360/150]">
            <DisegnoServizio slug={s.slug} />
          </span>
        </span>
        <span className="flex flex-col gap-1.5 px-6 py-5">
          <span className="font-sans text-voce font-semibold text-sommesso">Annuncio · Servizio {s.numero}</span>
          <span className="font-sans text-voce font-semibold text-segnale">{s.frase}</span>
          <b className="font-serif text-[24px] font-normal leading-[1.15] transition-colors group-hover/a:text-mare">{s.titolo}</b>
          <span className="line-clamp-2 font-sans text-sommario leading-[1.45] text-sommesso">{s.descrizione}</span>
          <span className="mt-1 font-sans text-corpo font-semibold text-mare">
            Scopri il servizio <span aria-hidden>↗</span>
          </span>
        </span>
      </Link>
    </aside>
  );
}

/**
 * La coda della spalla di una notizia aperta: l'editoriale della
 * settimana, la tabella dei report vicini al testo e qualche annuncio.
 * Richiesta del committente del 20/10/2026: la spalla finiva con «Dai
 * nostri servizi» e sotto, accanto a un testo lungo, restava il vuoto.
 *
 * Quante schede: tante quante ne stanno accanto al testo. Da `lg` la
 * griglia stira la spalla all'altezza della colonna del testo, e la coda
 * sta in una scatola `flex-1` con dentro un livello `absolute inset-0`:
 * così le schede non entrano nel conto dell'altezza della riga (la pagina
 * non si allunga per colpa loro, vedi LEARNINGS sugli `svg` in colonne
 * flex) e dopo l'idratazione si nascondono quelle che sforano. Nasconderle
 * non sposta niente, perché la scatola ha già la sua altezza: zero salti.
 * Un testo corto non lascia posto e la coda resta vuota; uno lungo ne
 * mostra fino a otto. Sotto `lg` la spalla va sotto il testo, il vuoto
 * non c'è e la coda non si rende.
 */
function ColonnaDiCoda({ coda }: { coda: CodaSpalla }) {
  const scatola = useRef<HTMLDivElement>(null);
  const r = coda.report;
  const a = coda.annunci;
  // L'ordine: editoriale, la tabella dei report vicini al testo, poi gli
  // annunci.
  const schede: { k: string; nodo: ReactNode }[] = [];
  if (coda.editoriale) schede.push({ k: "ed", nodo: <EditorialeSettimana e={coda.editoriale} colonna /> });
  if (r.length) schede.push({ k: "report", nodo: <TabellaReport righe={r} /> });
  for (const slug of a) schede.push({ k: `a${slug}`, nodo: <SchedaAnnuncio slug={slug} /> });

  useRiempiVuoto(scatola);

  if (schede.length === 0) return null;
  return (
    <div ref={scatola} aria-label="Da leggere ancora" role="region" className="relative hidden flex-1 lg:block">
      <div className="absolute inset-0 flex flex-col gap-10 overflow-hidden">
        {schede.map((x) => (
          <div key={x.k}>{x.nodo}</div>
        ))}
      </div>
    </div>
  );
}

/* ── Sotto il dispaccio ─────────────────────────────────────────── */

/* ── Sotto il riservato (tavola «Notizia riservata») ─────────────── */

/** La testata di una fascia della tavola chiusa (`.r-giorno`): titolo serif
 *  26 col segno, a destra una riga in mono 12 o un rimando, sopra un
 *  filetto d'inchiostro. Non è la `TestaSezione` della prima pagina (34
 *  pixel, filetto di 2): la tavola la usa solo per l'Atlante. */
function TestaFascia({ id, titolo, destra }: { id: string; titolo: string; destra?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-3 border-b border-ink pb-2.5">
      <h2 id={id} className={TITOLO_BLOCCO}>
        <SegnoTitolo />
        {titolo}
      </h2>
      {destra}
    </div>
  );
}

/** La foto in testa a una scheda (16:8). Senza foto a monte non c'è
 *  nessun riquadro: la scheda comincia dal testo (istruzioni, «mai un
 *  riquadro vuoto»). */
function FotoScheda({ src, className = "" }: { src: string | null; className?: string }) {
  if (!src) return null;
  return (
    <span className={`relative block aspect-[2/1] overflow-hidden bg-surface2 ${className}`}>
      <Image src={src} alt="" fill sizes="(min-width: 1440px) 440px, (min-width: 768px) 31vw, 100vw" className="object-cover" />
    </span>
  );
}

/** «Le notizie collegate» (`.nc-tre` di `.nc-card`): tre dispacci dello
 *  stesso territorio o degli stessi soggetti, con la foto in testa. Sono i
 *  dati di «Per approfondire» (`approfondimentiPer`), nella veste della
 *  tavola chiusa. */
function NotizieCollegate({ voci, paese }: { voci: Approfondimento[]; paese: string | null }) {
  if (voci.length === 0) return null;
  return (
    <section aria-labelledby="notizie-collegate">
      <TestaFascia
        id="notizie-collegate"
        titolo="Le notizie collegate"
        destra={
          <span className="font-mono text-meta text-sommesso">
            {paese ? `${etichettaLuogo(paese)} · ` : ""}stesso territorio, stessi soggetti, stesso tema
          </span>
        }
      />
      <ul className="m-0 mt-5 grid list-none grid-cols-1 gap-7 p-0 md:grid-cols-3">
        {voci.map((a) => {
          const tipo = a.tipoDerivato ? TIPO_DERIVATO[a.tipoDerivato].label : (PRIORITY_MAP[a.tipo]?.label ?? "");
          return (
            <li key={a.id} className="flex min-w-0">
              <Link href={a.href} className={`group/c flex w-full flex-col text-ink no-underline ${RIQUADRO} ${FUOCO}`}>
                <FotoScheda src={a.foto} />
                <span className="flex flex-1 flex-col gap-2.5 px-[22px] py-[18px]">
                  <span className={`inline-flex flex-wrap items-center gap-1.5 ${DATI_MONO}`}>
                    {a.territorio ? (
                      <>
                        <Icona nome="paese" className="size-4 text-mare" />
                        <b className="font-semibold text-ink">{etichettaLuogo(a.territorio)}</b>
                        <span aria-hidden>·</span>
                      </>
                    ) : null}
                    {PRIORITY_MAP[a.tipo]?.label ?? ""}
                    {a.tipoDerivato ? (
                      <>
                        <span aria-hidden>·</span>
                        {TIPO_DERIVATO[a.tipoDerivato].label}
                      </>
                    ) : null}
                  </span>
                  <span className="font-serif text-[20px] leading-[1.3] transition-colors group-hover/c:text-mare">
                    {a.titolo}
                  </span>
                  {a.sommario ? (
                    <span className="line-clamp-3 font-sans text-sommario leading-[1.5] text-sommesso">{a.sommario}</span>
                  ) : null}
                  <span className="mt-auto flex items-center justify-between pt-1">
                    <span className={`inline-flex items-center gap-[7px] ${DATI_MONO}`}>
                      <LogoOI variant="segno" decorative accento="fill-segnale" className="h-[13px] w-4 text-mare" />
                      {tipo} · {fmtDispatchId(a.id)}
                    </span>
                    <span aria-hidden className="font-sans text-corpo font-semibold text-mare">
                      ↗
                    </span>
                  </span>
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/** I servizi del riquadro grande, con la loro fantasia (il disegno della
 *  card del catalogo, `DisegnoServizio`) accanto al testo.
 *
 *  Dal 19/10/2026, su richiesta del committente, il riquadro non mostra
 *  sempre lo stesso numero di servizi: la pagina sorteggia anche quanti
 *  (da uno a tre, `servizi.vetrina`), e la forma segue il numero, così la
 *  fantasia cambia anche di misura da una visita all'altra:
 *
 *    - uno: il disegno a tutta colonna, sotto frase in rame, titolo
 *      grande, descrizione e invito;
 *    - due: due schede affiancate da `sm`, disegno in testa a ognuna;
 *    - tre: righe con il disegno piccolo a sinistra.
 *
 *  Il sorteggio è sul server (la rotta è dinamica): l'HTML arriva già
 *  nella sua forma, niente salti. */
function VetrinaServizi({ slugs }: { slugs: string[] }) {
  const voci = righeServizi(slugs, SERVIZI_RIQUADRO);
  if (voci.length === 0) return null;

  if (voci.length === 1) {
    const s = voci[0]!;
    const frase = servizioDi(s.slug)?.frase;
    return (
      <Link
        href={`/servizi/${s.slug}`}
        className={`group/s mt-1 flex flex-col gap-2.5 border-t border-ink pt-4 text-ink no-underline ${FUOCO}`}
      >
        <span className="block border border-rule">
          <DisegnoServizio slug={s.slug} />
        </span>
        {frase ? <span className={`mt-1 ${ETICHETTA_ACCESA}`}>{frase}</span> : null}
        <b className="block font-serif text-[28px] font-normal leading-[1.15] transition-colors group-hover/s:text-mare">
          {s.titolo}
        </b>
        <span className="block font-sans text-sommario leading-[1.5] text-sommesso">{s.testo}</span>
        <span className="font-sans text-corpo font-semibold text-mare">
          Scopri il servizio <span aria-hidden>↗</span>
        </span>
      </Link>
    );
  }

  if (voci.length === 2) {
    return (
      <ul className="m-0 mt-1 grid list-none grid-cols-1 gap-4 border-t border-ink p-0 pt-4 sm:grid-cols-2">
        {voci.map((s) => (
          <li key={s.slug} className="flex">
            <Link
              href={`/servizi/${s.slug}`}
              className={`group/s flex flex-1 flex-col gap-2 text-ink no-underline ${FUOCO}`}
            >
              <span className="block border border-rule">
                <DisegnoServizio slug={s.slug} />
              </span>
              <b className="mt-1 block font-serif text-lancio font-normal leading-[1.2] transition-colors group-hover/s:text-mare">
                {s.titolo}
              </b>
              <small className="block font-sans text-sommario leading-[1.45] text-sommesso">{s.testo}</small>
              <span aria-hidden className="mt-auto font-sans text-corpo font-semibold text-mare">
                ↗
              </span>
            </Link>
          </li>
        ))}
      </ul>
    );
  }

  return (
    <ul className="m-0 mt-1 list-none border-t border-ink p-0">
      {voci.map((s) => (
        <li key={s.slug} className="border-b border-rule">
          <Link
            href={`/servizi/${s.slug}`}
            className={`group/s grid grid-cols-[132px_minmax(0,1fr)_auto] items-center gap-4 py-3 text-ink no-underline max-sm:grid-cols-[96px_minmax(0,1fr)_auto] max-sm:gap-3 ${FUOCO}`}
          >
            <span className="block border border-rule">
              <DisegnoServizio slug={s.slug} />
            </span>
            <span>
              <b className="block font-serif text-lettura font-normal transition-colors group-hover/s:text-mare">
                {s.titolo}
              </b>
              <small className="mt-0.5 block font-sans text-sommario text-sommesso">{s.testo}</small>
            </span>
            <span aria-hidden className="font-sans text-corpo font-semibold text-mare">
              ↗
            </span>
          </Link>
        </li>
      ))}
    </ul>
  );
}

/** I testi brevi della tavola per il riquadro grande (`.nc-gc .sv`), per i
 *  quattro servizi che mostrava fissi fino al 19/10/2026; ora i quattro
 *  sono sorteggiati dalla pagina, e per gli altri valgono i testi del
 *  catalogo (`righeServizi`). */
const SERVIZI_RIQUADRO: Record<string, Omit<RigaServizio, "slug">> = {
  monitoraggio: { titolo: "Monitoraggio", testo: "Il tuo mercato, sotto osservazione: Paesi, filiere e controparti.", icona: "segnale" },
  "due-diligence": { titolo: "Due diligence", testo: "Chi c’è davvero dietro la controparte.", icona: "cerca" },
  analisi: { titolo: "Analisi", testo: "Mercati, costi e scenari in cifre.", icona: "report" },
  sanzioni: { titolo: "Gestione sanzioni", testo: "Con chi puoi lavorare, e con chi no.", icona: "sanzioni" },
};

/** La settimana ISO di una data, per «Editoriale · sett. 38». */
function settimanaIso(iso: string): number {
  const d = new Date(iso);
  const t = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  const g = t.getUTCDay() || 7;
  t.setUTCDate(t.getUTCDate() + 4 - g);
  const inizio = new Date(Date.UTC(t.getUTCFullYear(), 0, 1));
  return Math.ceil(((t.getTime() - inizio.getTime()) / 86400000 + 1) / 7);
}

/** Il riquadro grande (`.nc-gc`): a sinistra l'editoriale della settimana
 *  (foto in bianco e nero alta almeno 380, titolo in corsivo 34), al
 *  centro il sigillo, a destra «I nostri servizi». L'editoriale è lo
 *  stesso della prima pagina (`aperturaEditoriali().principale`); se non
 *  arriva, il riquadro resta con i soli servizi. */
function RiquadroEditoriale({ editoriale, slugs }: { editoriale: SchedaEditoriale | null; slugs: string[] }) {
  /* La foto dell'editoriale c'è sempre, se l'editoriale ne ha una. Fino al
     19/10/2026 le foto d'esempio (quelle con la `didascalia` «Immagine di
     esempio», lib/editoriali-foto-esempio.ts) qui non andavano, e il
     riquadro dei riservati restava testo su una colonna; il committente ha
     chiesto di vederla. Come in prima pagina (`Apertura`), la foto
     d'esempio lo dice sopra di sé con la sua didascalia, così non si legge
     come una scelta della redazione. */
  const foto = editoriale?.foto ?? null;
  return (
    <section
      aria-label="L’editoriale della settimana e i nostri servizi"
      className={`relative grid grid-cols-1 ${editoriale ? "lg:grid-cols-[minmax(0,1.25fr)_minmax(0,1fr)]" : ""} ${RIQUADRO}`}
    >
      {editoriale ? (
        <Link
          href={editoriale.href}
          className={`group/e grid grid-cols-1 border-rule text-ink no-underline lg:border-r ${foto ? "md:grid-cols-2" : ""} ${FUOCO}`}
        >
          {foto ? (
            <span className="relative block min-h-[240px] overflow-hidden bg-surface2 md:min-h-[380px]">
              <Image
                src={foto}
                alt=""
                fill
                sizes="(min-width: 1440px) 380px, (min-width: 768px) 28vw, 100vw"
                className="object-cover grayscale"
              />
              {editoriale.didascalia ? (
                <span className="absolute bottom-2 left-2 rounded-[2px] bg-ink/75 px-2 py-0.5 font-mono text-nota uppercase tracking-dato text-bg">
                  {editoriale.didascalia}
                </span>
              ) : null}
            </span>
          ) : null}
          {/* Senza foto il testo si centra in altezza e il piede lo segue:
              con la foto il piede sta in fondo, accanto alla sua base. */}
          <span className={`flex flex-col gap-3 px-[30px] pb-[26px] pt-[30px] lg:pr-12 ${foto ? "" : "justify-center"}`}>
            <span className="inline-flex items-center gap-2 font-sans text-voce font-semibold text-segnale">
              <Icona nome="editoriale" className="size-[18px] text-segnale" />
              L&rsquo;editoriale della settimana{editoriale.categoria ? ` · ${editoriale.categoria}` : ""}
            </span>
            <span className="font-serif text-[34px] italic leading-[1.1] transition-colors group-hover/e:text-mare max-md:text-fascia">
              {editoriale.titolo}
            </span>
            <span className="line-clamp-4 font-serif text-lettura leading-[1.5] text-sommesso">{editoriale.sommario}</span>
            {/* La controdomanda del pezzo, quando c'è: è una citazione, e
                le citazioni sono l'altro posto del corsivo. */}
            {editoriale.controdomanda ? (
              <span className="mt-1 border-l-[3px] border-segnale py-1 pl-4 font-serif text-lancio italic leading-[1.4] text-ink">
                «{editoriale.controdomanda}»
              </span>
            ) : null}
            <span className={`flex items-center justify-between ${foto ? "mt-auto" : "mt-3"}`}>
              <span className={`inline-flex items-center gap-[7px] ${DATI_MONO}`}>
                <LogoOI variant="segno" decorative accento="fill-segnale" className="h-[13px] w-4 text-mare" />
                Editoriale · sett. {settimanaIso(editoriale.iso)}
              </span>
              <span aria-hidden className="font-sans text-corpo font-semibold text-mare">
                ↗
              </span>
            </span>
          </span>
        </Link>
      ) : null}

      {editoriale ? (
        <span
          aria-hidden
          className="absolute left-[calc(1.25/2.25*100%)] top-1/2 z-[2] hidden size-16 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full border border-ink bg-bg shadow-[0_0_0_8px_var(--color-surface)] lg:grid"
        >
          <LogoOI variant="segno" decorative className="h-6 w-[30px] text-ink" />
        </span>
      ) : null}

      <div className={`flex flex-col gap-2.5 px-8 pb-[26px] pt-[30px] max-md:px-5 ${editoriale ? "lg:pl-[52px]" : ""}`}>
        <p className={`m-0 ${ETICHETTA_ACCESA}`}>I nostri servizi</p>
        <h2 className={TITOLO_BLOCCO}>Osservare. Comprendere. Decidere.</h2>
        <p className="m-0 font-sans text-sommario text-sommesso">
          L&rsquo;Osservatorio affianca le imprese italiane prima delle decisioni.
        </p>
        <VetrinaServizi slugs={slugs} />
        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-2.5">
          <Link
            href="/contatti"
            className={`inline-flex items-center gap-2 rounded-[2px] bg-mare px-[18px] py-[11px] font-sans text-corpo font-semibold text-suMare no-underline transition-opacity hover:opacity-90 ${FUOCO}`}
          >
            Apri una conversazione <span aria-hidden>↗</span>
          </Link>
          <span className="font-sans text-voce text-sommesso">Riservata, sotto NDA</span>
        </div>
      </div>
    </section>
  );
}

/** «Altri casi aperti» (`.nc-caso3`): tre filoni in corso diversi da
 *  questo, ognuno presentato dalla sua ultima tappa, con la foto a tutta
 *  larghezza in testa (16:8), «Il caso» nel rame, paese e rubrica, titolo
 *  serif 26, e in fondo i rombi e l'ultima tappa.
 *
 *  La tavola ha anche «Tutti i casi ↗»: non c'è, perché una pagina dei
 *  casi non esiste (vedi `TIPO_DERIVATO` in lib/constants.ts). */
function AltriCasi({ voci }: { voci: AltroCaso[] }) {
  if (voci.length === 0) return null;
  return (
    <section aria-labelledby="altri-casi">
      <TestaFascia id="altri-casi" titolo="Altri casi aperti" />
      <ul className="m-0 mt-5 grid list-none grid-cols-1 gap-7 p-0 md:grid-cols-3">
        {voci.map((c) => {
          const icona = c.categoria ? iconaCategoria(c.categoria) : null;
          return (
            <li key={c.id} className="flex min-w-0">
              <Link
                href={c.href}
                className={`group/c flex w-full flex-col gap-2.5 overflow-hidden px-[22px] pb-[18px] text-ink no-underline ${c.foto ? "" : "pt-[18px]"} ${RIQUADRO} ${FUOCO}`}
              >
                <FotoScheda src={c.foto} className="-mx-[22px] mb-2" />
                {/* Etichetta a 15 (era 13): stessa richiesta della riga del caso. */}
                <span className="inline-flex items-center gap-2 font-sans text-sommario font-semibold text-segnale">
                  <Icona nome="caso" className="size-[17px] text-segnale" />
                  {ETICHETTA_VISTA[c.tipoDerivato]}
                </span>
                {c.paese || icona ? (
                  <span className={`inline-flex flex-wrap items-center gap-1.5 ${DATI_MONO}`}>
                    {c.paese ? (
                      <>
                        <Icona nome="paese" className="size-4 text-mare" />
                        <b className="font-semibold text-ink">{etichettaLuogo(c.paese)}</b>
                      </>
                    ) : null}
                    {c.paese && icona ? <span aria-hidden>·</span> : null}
                    {icona ? (
                      <>
                        <Icona nome={icona} tono={TONO_ICONA[icona]} />
                        {c.categoria}
                      </>
                    ) : null}
                  </span>
                ) : null}
                <span className="font-serif text-[26px] leading-[1.2] transition-colors group-hover/c:text-mare">
                  {c.nome}
                </span>
                {c.testo ? (
                  <span className="line-clamp-3 font-sans text-sommario leading-[1.5] text-sommesso">{c.testo}</span>
                ) : null}
                <span className="mt-auto flex items-center gap-3 border-t border-rule pt-2.5 font-mono text-meta text-sommesso">
                  <TracciaRombi pezzi={c.tappe} larga />
                  <span>
                    {c.tappe >= 2 ? `${c.tappe} tappe · ` : ""}ultima il{" "}
                    <time dateTime={c.ultima.iso}>{c.ultima.testo}</time>
                  </span>
                  <span aria-hidden className="ml-auto font-sans text-corpo font-semibold text-mare">
                    ↗
                  </span>
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/* ── La pagina ──────────────────────────────────────────────────── */

/** Quanto bianco in più dello scarto minimo «Dai nostri servizi» può
 *  assorbire pur di chiudere i gruppi più in basso (vedi
 *  `bilanciaColonne`): circa due voci di servizio. */
const SCARTO_TOLLERATO = 160;

export function DispaccioView({
  item,
  sommarioRiservato,
  seguitoRiservato,
  paese,
  pubblicato,
  aggiornato,
  approfondimenti,
  altriCasi,
  rulli,
  caso,
  editoriale,
  atlante,
  servizi,
  hrefCondivisione,
  annuncio,
  tappeInColonna = false,
  coda,
}: Props) {
  /* Il sommario sotto il titolo. Sui riservati è il sommario pubblico a
     frasi intere che arriva dal server. Sugli aperti il `lead` sarebbe
     l'attacco del corpo tagliato a 260 caratteri e ripetuto subito
     sotto: il sommario è allora il primo capoverso, e il corpo comincia
     dal secondo (senza maiuscoletto sulla prima riga dal 20/10/2026). Con un
     capoverso solo non c'è sommario: è tutto corpo. I sottotitoli
     (`blocchiDelCorpo`) restano nel corpo, mai nel sommario. */
  const blocchi = useMemo(() => (item.free ? blocchiDelCorpo(item.body) : []), [item]);
  const apreConSommario = blocchi.length > 1 && blocchi[0].tipo === "p";
  /* Dal modello Zijin del 20/10/2026 il sommario è la sola prima frase
     del primo capoverso; quello che avanza del capoverso apre il corpo. */
  const attacco = apreConSommario ? primaFrase(blocchi[0].testo) : null;
  const deck = item.free ? (attacco ? attacco.frase : null) : (sommarioRiservato ?? item.lead);
  const corpo = item.free
    ? attacco
      ? [...(attacco.resto ? [{ tipo: "p" as const, testo: attacco.resto }] : []), ...blocchi.slice(1)]
      : blocchi
    : [];

  /* Le due colonne di un riservato finiscono alla stessa altezza
     (istruzioni, 5). La spalla quasi sempre è più lunga del testo, che di
     un riservato è solo sommario, porta e cronologia: «Chi e dove» da
     solo arriva a 1.200 pixel, e sotto la cronologia restavano 350-480
     pixel di bianco (misurati su OI-353578 e OI-165). Allora, da `lg`:
     si chiudono i gruppi di nomi che servono, scegliendo la combinazione
     che lascia meno scarto e, a pari scarto, i gruppi più in basso (i
     primi restano aperti, e sopra la piega non si muove niente); il resto
     lo assorbe «Dai nostri servizi», che si allunga col bottone in fondo.
     I gruppi chiusi restano apribili, con il conto nella riga di testa.
     Gira al montaggio, quando i caratteri sono arrivati e a ogni
     ridimensionamento; sotto `lg` le colonne sono una e i gruppi tornano
     tutti aperti. */
  const sinistra = useRef<HTMLDivElement>(null);
  const spalla = useRef<HTMLElement>(null);
  useEffect(() => {
    if (item.free) return;
    const bilanciaColonne = () => {
      const sx = sinistra.current;
      const dx = spalla.current;
      if (!sx || !dx) return;
      const gruppi = [...dx.querySelectorAll<HTMLDetailsElement>("details[data-gruppo]")];
      gruppi.forEach((g) => (g.open = true));
      if (!window.matchMedia("(min-width: 64rem)").matches) return;
      // Le altezze naturali: con la griglia stirata le due colonne sono
      // alte uguali per definizione.
      sx.style.alignSelf = dx.style.alignSelf = "start";
      const avanzo = dx.offsetHeight - sx.offsetHeight;
      const tagli = gruppi.map((g) => g.offsetHeight - (g.querySelector("summary")?.offsetHeight ?? 0) - 1);
      sx.style.alignSelf = dx.style.alignSelf = "";
      if (avanzo <= 0) return;
      /* Si chiude dopo l'idratazione, quindi tutto quello che sta sotto
         un gruppo chiuso sale: chiudere «Persone», in cima alla spalla e
         sopra la piega, spostava mezza schermata (CLS 0,047 su OI-353578 a
         1440, contro 0,008 chiudendo i gruppi più in basso). Allora, fra le
         combinazioni che lasciano al più `SCARTO_TOLLERATO` pixel in più
         dello scarto minimo (li assorbe «Dai nostri servizi»), vince quella
         il cui primo gruppo chiuso sta più in basso. */
      const combinazioni: { idx: number[]; somma: number; primo: number }[] = [];
      for (let m = 1; m < 1 << gruppi.length; m++) {
        const idx = gruppi.map((_, i) => i).filter((i) => m & (1 << i));
        const somma = idx.reduce((t, i) => t + tagli[i], 0);
        if (somma >= avanzo) combinazioni.push({ idx, somma, primo: Math.min(...idx) });
      }
      const minimo = Math.min(...combinazioni.map((c) => c.somma));
      const scelta =
        combinazioni
          .filter((c) => c.somma <= minimo + SCARTO_TOLLERATO)
          .sort((a, b) => b.primo - a.primo || a.somma - b.somma)[0]?.idx ?? null;
      (scelta ?? gruppi.map((_, i) => i)).forEach((i) => (gruppi[i].open = false));
    };
    bilanciaColonne();
    document.fonts?.ready.then(bilanciaColonne);
    let attesa: ReturnType<typeof setTimeout> | undefined;
    const suRidimensiona = () => {
      clearTimeout(attesa);
      attesa = setTimeout(bilanciaColonne, 150);
    };
    window.addEventListener("resize", suRidimensiona);
    return () => {
      clearTimeout(attesa);
      window.removeEventListener("resize", suRidimensiona);
    };
  }, [item.free]);

  /* Il vuoto fra un blocco e l'altro è 60, quello delle due tavole
     (`main` a `gap: 60px`). */
  return (
    <div className={`${CORNICE_HOME} flex flex-col gap-[60px] pb-[60px] pt-band`}>
      <article aria-labelledby="titolo-dispaccio">
        <div
          className={`grid grid-cols-1 gap-10 lg:grid-cols-[minmax(0,1fr)_340px] xl:grid-cols-[minmax(0,1fr)_420px] xl:gap-14 ${
            item.free && !coda ? "items-start" : "items-stretch"
          }`}
        >
          <div ref={sinistra} className={`min-w-0 ${item.free && coda ? "lg:flex lg:flex-col" : ""}`}>
            {/* Il contenuto resta un blocco (i margini fra i pezzi si
                comportano come prima); sotto, da `lg`, il riempitivo che
                prende il bianco quando la spalla è più lunga. */}
            <div>
            {annuncio}
            <TestaDispaccio
              item={item}
              paese={paese}
              pubblicato={pubblicato}
              aggiornato={aggiornato}
              deck={deck}
              caso={caso}
              hrefCondivisione={hrefCondivisione}
            />
            {item.free ? (
              <>
                <Foto item={item} />
                <CorpoNotizia blocchi={corpo} />
                {tappeInColonna ? <ComeSiEArrivati item={item} caso={caso} /> : null}
              </>
            ) : (
              <>
                <BloccoChiuso item={item} attacco={seguitoRiservato ?? ""} caso={caso} />
                {/* Sull'aperto le tappe stanno nella fascia «Il caso» sotto
                    l'articolo (`IlCasoAperto`), come nel modello del 19/10. */}
                <CronologiaCaso item={item} caso={caso} />
              </>
            )}
            </div>
            {item.free && coda ? <RiempiTesto servizi={coda.sottoTesto} /> : null}
          </div>

          <aside
            ref={spalla}
            aria-label="Per capire il dispaccio"
            className="flex min-w-0 flex-col gap-10 max-lg:border-t max-lg:border-rule max-lg:pt-8 lg:border-l lg:border-rule lg:pl-8 xl:pl-10"
          >
            {/* Nessuna mappa: né il modello aperto del 19/10 né la tavola
                chiusa hanno un blocco «Dove», e i luoghi stanno già in «Chi
                e dove». */}
            {item.free ? (
              <>
                <InBreveAperto item={item} paese={paese} />
                <ChiEDoveAperto item={item} />
              </>
            ) : (
              <>
                <Contesto item={item} paese={paese} caso={caso} />
                <ChiEDove item={item} riservato />
              </>
            )}
            <ServiziSpalla paese={paese} allunga={!item.free} slugs={servizi.spalla} />
            {item.free && coda ? <ColonnaDiCoda coda={coda} /> : null}
          </aside>
        </div>
      </article>

      {item.free ? (
        <>
          {tappeInColonna ? null : <IlCasoAperto item={item} caso={caso} servizi={servizi.riquadro} />}
          <PerApprofondireAperto voci={approfondimenti} paese={paese} />
          <RulliAperti rulli={rulli} paese={paese} />
        </>
      ) : (
        /* Sotto un riservato, nell'ordine della tavola chiusa: le
           notizie collegate, il riquadro con l'editoriale e i servizi, gli
           altri casi aperti, l'Atlante. Niente «Il lavoro dietro il
           report» né «Per approfondire»: sono della notizia aperta. */
        <>
          <NotizieCollegate voci={approfondimenti} paese={paese} />
          <RiquadroEditoriale editoriale={editoriale} slugs={servizi.vetrina} />
          <AltriCasi voci={altriCasi} />
          {atlante}
        </>
      )}
    </div>
  );
}
